import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  getOrganizationPrivateMoneyFee,
  showOrganization,
} from '~/javascripts/admin/actions/organization';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import Grid from '../../atoms/Grid';
import BreadCrumb from '../../molecules/BreadCrumb';
import MoneyFeeDisplay from './MoneyFeeDisplay';

type Organization = Awaited<ReturnType<typeof showOrganization>>;

type MoneyFee = {
  payment_fee_rate_percentage: string;
  system_fee: string;
  fixed_fee: string;
};

type MoneyFeeData = {
  moneyId: string;
  moneyName: string | undefined;
  fees: MoneyFee;
};

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '各種設定', href: '', isCurrentPage: true },
  { value: '組織情報', href: '', isCurrentPage: true },
];

const BANK_ACCOUNT_TYPES = {
  saving: '普通',
  current: '当座',
  other: 'その他',
};

const PageWrapper = styled.div``;

const PageHeader = styled.h1`
  ${({
    theme: {
      typography: { heading3 },
      colors,
    },
  }) => css`
    font-size: ${heading3.bold.fontSize};
    font-weight: ${heading3.bold.fontWeight};
    line-height: ${heading3.bold.lineHeight};
    color: ${colors.gray['400']};
  `}
`;

const SectionWrapper = styled.div`
  ${({ theme: { spacing } }) => css`
    margin: ${spacing['24']} 0;
  `}
`;

const SectionHeader = styled.h2`
  ${({
    theme: {
      typography: { body1 },
      colors,
    },
  }) => css`
    font-size: ${body1.regular.fontSize};
    font-weight: ${body1.regular.fontWeight};
    line-height: ${body1.regular.lineHeight};
    color: ${colors.gray['400']};
  `}
`;

const SectionGrid = styled(Grid.Container)`
  ${({
    theme: {
      spacing,
      typography: { body2 },
    },
  }) => css`
    padding-left: ${spacing['8']};

    ${Grid.Item} {
      padding: ${spacing['4']};
      font-size: ${body2.regular.fontSize};
      line-height ${body2.regular.lineHeight};
    }
  `}
`;

const MoneyFeeContainer = styled.div`
  ${({ theme: { spacing } }) => css`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing['12']};
  `}
`;

const SelfOrganizationDetail = () => {
  const user = useAppSelector((state) => state.user);
  const orgCode = user.organization?.code;

  const [organization, setOrganization] = useState<Organization>();
  const [moneyFees, setMoneyFees] = useState<MoneyFeeData[]>([]);
  const [isLoadingFees, setIsLoadingFees] = useState(true);

  const isIssuer =
    organization?.private_moneys?.some((money) => money.organization?.code === orgCode) ?? false;
  const hideMoneyFees = isIssuer && user.isOrgUser();

  useEffect(() => {
    if (orgCode) {
      showOrganization(orgCode).then((org) => {
        setOrganization(org);

        if (org.private_moneys && org.private_moneys.length > 0) {
          const moneys = org.private_moneys.filter(
            (money) => money.organization?.code !== 'pocketchange'
          );

          const feePromises = moneys.map((money) =>
            getOrganizationPrivateMoneyFee(org.code, money.id)
              .then((feeData) => ({
                moneyId: money.id,
                moneyName: money.name,
                fees: {
                  payment_fee_rate_percentage: feeData.payment_fee_rate_percentage.toString(),
                  system_fee: feeData.system_fee.toString(),
                  fixed_fee: feeData.fixed_fee.toString(),
                },
              }))
              .catch((error) => {
                console.error(`Error fetching fees for money ${money.id}:`, error);
                return {
                  moneyId: money.id,
                  moneyName: money.name,
                  fees: {
                    payment_fee_rate_percentage: '0',
                    system_fee: '0',
                    fixed_fee: '0',
                  },
                };
              })
          );

          Promise.all(feePromises).then((results) => {
            setMoneyFees(results);
            setIsLoadingFees(false);
          });
        } else {
          setIsLoadingFees(false);
        }
      });
    }
  }, [orgCode]);

  return (
    <PageWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      <PageHeader>組織情報</PageHeader>
      <SectionWrapper>
        <SectionHeader>基本情報</SectionHeader>
        <SectionGrid>
          <Grid.Item size={3}>組織コード</Grid.Item>
          <Grid.Item size={9}>{organization?.code}</Grid.Item>
          <Grid.Divider />
          <Grid.Item size={3}>組織名</Grid.Item>
          <Grid.Item size={9}>{organization?.name}</Grid.Item>
          <Grid.Divider />
          <Grid.Item size={3}>担当者名</Grid.Item>
          <Grid.Item size={9}>{organization?.contact_name}</Grid.Item>
          <Grid.Divider />
        </SectionGrid>
      </SectionWrapper>

      <SectionWrapper>
        <SectionHeader>口座情報</SectionHeader>
        <SectionGrid>
          <Grid.Item size={3}>金融機関名</Grid.Item>
          <Grid.Item size={9}>{organization?.bank_name}</Grid.Item>
          <Grid.Divider />
          <Grid.Item size={3}>支店名</Grid.Item>
          <Grid.Item size={9}>{organization?.bank_branch_name}</Grid.Item>
          <Grid.Divider />
          <Grid.Item size={3}>支店コード</Grid.Item>
          <Grid.Item size={9}>{organization?.bank_branch_code}</Grid.Item>
          <Grid.Divider />
          <Grid.Item size={3}>口座種別</Grid.Item>
          {organization && (
            <Grid.Item size={9}>{BANK_ACCOUNT_TYPES[organization.bank_account_type]}</Grid.Item>
          )}
          <Grid.Divider />
          <Grid.Item size={3}>口座番号</Grid.Item>
          <Grid.Item size={9}>{organization?.bank_account}</Grid.Item>
          <Grid.Divider />
          <Grid.Item size={3}>口座名義人</Grid.Item>
          <Grid.Item size={9}>{organization?.bank_account_holder_name}</Grid.Item>
          <Grid.Divider />
        </SectionGrid>
      </SectionWrapper>

      {!hideMoneyFees && (
        <SectionWrapper>
          <SectionHeader>各種費用</SectionHeader>
          {isLoadingFees ? (
            <div>読み込み中...</div>
          ) : (
            <MoneyFeeContainer>
              {moneyFees.map((moneyFee) => (
                <MoneyFeeDisplay
                  key={moneyFee.moneyId}
                  moneyName={moneyFee.moneyName}
                  paymentFeeRate={moneyFee.fees.payment_fee_rate_percentage}
                  systemFee={moneyFee.fees.system_fee}
                  fixedFee={moneyFee.fees.fixed_fee}
                />
              ))}
            </MoneyFeeContainer>
          )}
        </SectionWrapper>
      )}
    </PageWrapper>
  );
};

export default SelfOrganizationDetail;
